// Phase-1 Touchpoints — React-Query hooks.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { ACCOUNTS_QUERY_KEY } from "@/features/accounts";

import {
  cancelTouchpoint,
  completeTouchpoint,
  createAccountTouchpoint,
  deleteTouchpoint,
  deleteTouchpointRecording,
  fetchAccountTouchpoints,
  fetchTouchpoint,
  fetchTouchpointRecordings,
  fetchTouchpointStats,
  markTouchpointNoShow,
  updateTouchpoint,
  uploadTouchpointRecording,
} from "./api";
import type {
  Touchpoint,
  TouchpointCreateInput,
  TouchpointListResponse,
  TouchpointRecording,
  TouchpointRecordingListResponse,
  TouchpointStatsResponse,
  TouchpointUpdateInput,
} from "./types";


export const TOUCHPOINTS_QUERY_KEY = "touchpoints" as const;
export const TOUCHPOINT_STATS_QUERY_KEY = "touchpoint-stats" as const;


function _invalidateForAccount(
  qc: ReturnType<typeof useQueryClient>, accountId: string,
  touchpointId?: string,
) {
  qc.invalidateQueries({
    queryKey: [TOUCHPOINTS_QUERY_KEY, "account", accountId],
  });
  qc.invalidateQueries({
    queryKey: [ACCOUNTS_QUERY_KEY, "detail", accountId],
  });
  qc.invalidateQueries({ queryKey: [TOUCHPOINT_STATS_QUERY_KEY] });
  if (touchpointId) {
    qc.invalidateQueries({
      queryKey: [TOUCHPOINTS_QUERY_KEY, "detail", touchpointId],
    });
  }
}


export function useAccountTouchpointsQuery(
  accountId: string | undefined,
): UseQueryResult<TouchpointListResponse> {
  return useQuery({
    queryKey: [TOUCHPOINTS_QUERY_KEY, "account", accountId],
    queryFn: ({ signal }) =>
      fetchAccountTouchpoints(accountId!, { signal }),
    enabled: Boolean(accountId),
  });
}


export function useTouchpointDetailQuery(
  touchpointId: string | undefined,
): UseQueryResult<Touchpoint> {
  return useQuery({
    queryKey: [TOUCHPOINTS_QUERY_KEY, "detail", touchpointId],
    queryFn: ({ signal }) => fetchTouchpoint(touchpointId!, { signal }),
    enabled: Boolean(touchpointId),
  });
}


export function useCreateAccountTouchpointMutation(
  accountId: string,
): UseMutationResult<Touchpoint, Error, TouchpointCreateInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TouchpointCreateInput) =>
      createAccountTouchpoint(accountId, input),
    onSuccess: () => _invalidateForAccount(qc, accountId),
  });
}


export interface UpdateTouchpointVars {
  touchpoint_id: string;
  account_id: string;
  values: TouchpointUpdateInput;
}


export function useUpdateTouchpointMutation(): UseMutationResult<
  Touchpoint, Error, UpdateTouchpointVars
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: UpdateTouchpointVars) =>
      updateTouchpoint(vars.touchpoint_id, vars.values),
    onSuccess: (_data, vars) =>
      _invalidateForAccount(qc, vars.account_id, vars.touchpoint_id),
  });
}


export interface LifecycleVars {
  touchpoint_id: string;
  account_id: string;
  outcome_summary?: string;
  reason?: string;
}


export function useCompleteTouchpointMutation(): UseMutationResult<
  Touchpoint, Error, LifecycleVars
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: LifecycleVars) =>
      completeTouchpoint(vars.touchpoint_id, {
        outcome_summary: vars.outcome_summary,
      }),
    onSuccess: (_data, vars) =>
      _invalidateForAccount(qc, vars.account_id, vars.touchpoint_id),
  });
}


export function useCancelTouchpointMutation(): UseMutationResult<
  Touchpoint, Error, LifecycleVars
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: LifecycleVars) =>
      cancelTouchpoint(vars.touchpoint_id, { reason: vars.reason }),
    onSuccess: (_data, vars) =>
      _invalidateForAccount(qc, vars.account_id, vars.touchpoint_id),
  });
}


export function useNoShowTouchpointMutation(): UseMutationResult<
  Touchpoint, Error, LifecycleVars
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: LifecycleVars) =>
      markTouchpointNoShow(vars.touchpoint_id),
    onSuccess: (_data, vars) =>
      _invalidateForAccount(qc, vars.account_id, vars.touchpoint_id),
  });
}


export function useDeleteTouchpointMutation(): UseMutationResult<
  { touchpoint_id: string; deleted: boolean },
  Error,
  LifecycleVars
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: LifecycleVars) => deleteTouchpoint(vars.touchpoint_id),
    onSuccess: (_data, vars) =>
      _invalidateForAccount(qc, vars.account_id, vars.touchpoint_id),
  });
}


export function useTouchpointStatsQuery(
  days?: number,
): UseQueryResult<TouchpointStatsResponse> {
  return useQuery({
    queryKey: [TOUCHPOINT_STATS_QUERY_KEY, days ?? 7],
    queryFn: ({ signal }) => fetchTouchpointStats({ signal, days }),
  });
}


// ── Recordings (Phase-1 chunk 2) ─────────────────────────────────────────


export const TOUCHPOINT_RECORDINGS_QUERY_KEY =
  "touchpoint-recordings" as const;


function _invalidateRecordingsAndDetail(
  qc: ReturnType<typeof useQueryClient>,
  touchpointId: string,
  accountId?: string,
) {
  qc.invalidateQueries({
    queryKey: [TOUCHPOINT_RECORDINGS_QUERY_KEY, touchpointId],
  });
  qc.invalidateQueries({
    queryKey: [TOUCHPOINTS_QUERY_KEY, "detail", touchpointId],
  });
  if (accountId) {
    qc.invalidateQueries({
      queryKey: [TOUCHPOINTS_QUERY_KEY, "account", accountId],
    });
  }
}


export function useTouchpointRecordingsQuery(
  touchpointId: string | undefined,
): UseQueryResult<TouchpointRecordingListResponse> {
  return useQuery({
    queryKey: [TOUCHPOINT_RECORDINGS_QUERY_KEY, touchpointId],
    queryFn: ({ signal }) =>
      fetchTouchpointRecordings(touchpointId!, { signal }),
    enabled: Boolean(touchpointId),
    // While any row is still ``pending`` or ``transcribing``, poll
    // every 4s so the panel flips to ``succeeded`` without the user
    // refreshing. Stops as soon as everything terminal.
    refetchInterval: (query) => {
      const rows = query.state.data?.rows ?? [];
      const pending = rows.some(
        (r) =>
          r.transcription_status === "pending"
          || r.transcription_status === "transcribing",
      );
      return pending ? 4000 : false;
    },
  });
}


export interface UploadRecordingVars {
  touchpoint_id: string;
  account_id?: string;
  file: File;
}


export function useUploadTouchpointRecordingMutation(): UseMutationResult<
  TouchpointRecording, Error, UploadRecordingVars
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) =>
      uploadTouchpointRecording(vars.touchpoint_id, vars.file),
    onSuccess: (_data, vars) =>
      _invalidateRecordingsAndDetail(
        qc, vars.touchpoint_id, vars.account_id,
      ),
  });
}


export interface DeleteRecordingVars {
  recording_id: string;
  touchpoint_id: string;
  account_id?: string;
}


export function useDeleteTouchpointRecordingMutation(): UseMutationResult<
  { recording_id: string; deleted: boolean }, Error, DeleteRecordingVars
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) => deleteTouchpointRecording(vars.recording_id),
    onSuccess: (_data, vars) =>
      _invalidateRecordingsAndDetail(
        qc, vars.touchpoint_id, vars.account_id,
      ),
  });
}
